import {
  Route,
  Switch,
  useLocation,
  useHistory,
  Redirect,
} from 'react-router-dom';
import { MARKETPLACE_DEVELOPER_ROUTES } from '../config/routes';
import { useFeature } from 'lib/featureFlags/features';
import { useAzureFeatureFlags } from 'lib/featureFlags/azure-feature-flags';
import { LoadingScreen } from 'features/common/components/LoadingScreen';
import { fetchLastSuccessfulLoginOrganizationId } from '../../common/auth/auth0Storage';
import { getBaseMarketplaceAppUrl } from '../config/env';
import { useFetchTenantsData } from '../core/auth/hooks/useFetchTenantsData';
import { IframeWithNavigationSync } from 'features/common/components/IframeWithNavigationSync';
import { useEffect } from 'react';

export const DeveloperRoutes: React.FC = () => {
  const { isLoading } = useAzureFeatureFlags({ enabled: true });
  const featureEnabled = useFeature('MARKETPLACE');
  const location = useLocation();
  const history = useHistory();

  const { tenantsData, isLoading: tenantsLoading } = useFetchTenantsData();

  // Check if current path contains '/custom-app'
  const isCustomAppPath = location.pathname.includes('/custom-app');

  // Check for appId in query parameters
  const searchParams = new URLSearchParams(location.search);
  const appId = searchParams.get('appId');

  // Remove appId from URL if we're on /developer but not /custom-app
  useEffect(() => {
    if (!isCustomAppPath && appId) {
      // Remove appId from query parameters
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('appId');

      // Construct new URL without appId
      const newSearch = newSearchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      // Replace current URL without appId
      history.replace(newUrl);
    }
  }, [isCustomAppPath, appId, location.search, location.pathname, history]);

  if (isLoading || tenantsLoading) {
    return <LoadingScreen data-testid='loading-component' message='Loading…' />;
  }
  if (!featureEnabled && !isLoading) {
    return <Redirect to='/no-access' />;
  }

  const marketplaceTenant = tenantsData[0]?.id;
  const organizationId = fetchLastSuccessfulLoginOrganizationId();
  const marketplaceAppUrl = getBaseMarketplaceAppUrl();

  // Construct the appropriate URL based on whether we're on custom-app path AND have appId
  const iframeUrl =
    isCustomAppPath && appId
      ? `${marketplaceAppUrl}/developer/custom-app?organization=${organizationId}&tenantId=${marketplaceTenant}&appId=${appId}`
      : `${marketplaceAppUrl}/developer?organization=${organizationId}&tenantId=${marketplaceTenant}`;

  return (
    <Route path={MARKETPLACE_DEVELOPER_ROUTES.ROOT}>
      <Switch>
        <Route
          path={[
            MARKETPLACE_DEVELOPER_ROUTES.ROOT,
            MARKETPLACE_DEVELOPER_ROUTES.CUSTOM_APP,
          ]}
          render={() => (
            <IframeWithNavigationSync
              src={iframeUrl}
              title='Marketplace Developer Iframe'
              allow='clipboard-read; clipboard-write'
              debugMode={process.env.NODE_ENV === 'development'}
            />
          )}
        />
      </Switch>
    </Route>
  );
};
